/**
 * Does the crew schedule agree with the project about when this job starts?
 *
 * Two people own two dates that answer the same question, and nothing has ever
 * compared them (#385, and #288 asks for the same alignment from the other end):
 *
 *   • the PM owns `projects.current_start_date` — MIN(planned_start) since #380,
 *     "the start we are currently working to" — edited on the Schedule & Dates
 *     card, and it is what the Projects list shows.
 *   • the Scheduler owns `mp_schedule_entries` — the day crew is actually booked
 *     on, edited on the Crew Schedule grid.
 *
 * Job 10943 is the shape: crew booked from 2026-09-28, planned start 2026-10-05.
 * Neither surface showed the other's date, so a seven-day disagreement sat there
 * with both owners believing their own screen.
 *
 * ⚠️ THIS FLAGS, IT DOES NOT RECONCILE. #385 asks for "an automation that forces
 * alignment", and writing one date from the other would be the wrong answer to
 * it: the two fields have different owners and different permissions, and a
 * silent overwrite would let either role rewrite the other's work without
 * knowing. Surfacing the disagreement on BOTH screens puts it in front of the
 * two people who can resolve it, which is the outcome the automation was after.
 *
 * ⚠️ ANY difference counts, in either direction. The two dates are answering the
 * same question — "when does this job start" — so there is no gap that is
 * legitimately fine, and no threshold to defend. A job whose planned start has
 * passed with nobody booked is as wrong as one with crew on site a week early.
 */

/** A disagreement between the plan and the crew booking. */
export interface StartMismatch {
  /** `projects.current_start_date` — the PM's date. YYYY-MM-DD. */
  plannedStart: string;
  /** First day with published crew booked — the Scheduler's date. YYYY-MM-DD. */
  firstCrewDate: string;
  /**
   * Calendar days from the plan to the first crew day. NEGATIVE means crew is
   * booked BEFORE the job is planned to start (the 10943 shape); positive means
   * the planned start passes with nobody booked until later.
   */
  driftDays: number;
  /** Which way round it is, for copy that has to name a direction. */
  direction: 'crew-early' | 'crew-late';
}

const YMD = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Date-only prefix, or null when there isn't a usable one.
 *
 * ⚠️ The shape check is not a validity check, and neither is parsing. Both
 * matter here:
 *
 *   • '2026-13-99' satisfies the pattern and parses to NaN, which would produce
 *     a mismatch whose `driftDays` was NaN — "NaN days" in a banner telling two
 *     people to go and talk to each other.
 *   • '2026-02-30' satisfies the pattern AND parses, silently rolling over to
 *     2026-03-02. It would have flagged an aligned job, two days out.
 *
 * So the date has to round-trip: parse it, format it back, and require the same
 * string. Both dates come from SQL `DATE` columns today and neither shape is
 * reachable from there — this is here so that stays true if a caller ever hands
 * over something hand-built.
 */
function ymd(value: string | null | undefined): string | null {
  const s = (value ?? '').slice(0, 10);
  if (!YMD.test(s)) return null;
  const ms = Date.parse(`${s}T00:00:00Z`);
  if (!Number.isFinite(ms)) return null;
  return new Date(ms).toISOString().slice(0, 10) === s ? s : null;
}

/**
 * The mismatch between a job's planned start and its first booked crew day, or
 * `null` when there isn't one to report.
 *
 * Null covers three different situations, and none of them is a disagreement:
 *
 *   • THE DATES MATCH. The ordinary, healthy case.
 *   • NO CREW IS BOOKED AT ALL. Nothing to compare against, and the grid already
 *     has a home for this — the "⚠ Needs Scheduling" section, for a job starting
 *     inside the visible window with no crew. Flagging it here as well would
 *     double-report one problem on one row.
 *   • NO PLANNED START. A job with no date on the project has nothing for the
 *     schedule to disagree WITH; that is a gap in the project, not a conflict.
 *
 * ⚠️ Compared as YYYY-MM-DD strings and differenced in UTC, so a viewer's
 * timezone cannot shift a calendar date across a boundary and invent a one-day
 * drift on a job that is actually aligned.
 */
export function startMismatch(
  plannedStart: string | null | undefined,
  firstCrewDate: string | null | undefined,
): StartMismatch | null {
  const plan = ymd(plannedStart);
  const crew = ymd(firstCrewDate);
  if (!plan || !crew || plan === crew) return null;
  const driftDays = Math.round(
    (Date.parse(`${crew}T00:00:00Z`) - Date.parse(`${plan}T00:00:00Z`)) / 86_400_000,
  );
  return {
    plannedStart: plan,
    firstCrewDate: crew,
    driftDays,
    direction: driftDays < 0 ? 'crew-early' : 'crew-late',
  };
}

/**
 * Is this a finished job — one the crew grid drops, so its start never flags?
 *
 * A finished job's start date is history, not something two people still have
 * to reconcile, so neither screen flags it (product owner decision, #385
 * follow-up to #540). The grid gets there by filtering Complete and Canceled
 * out of its project list in `useScheduleData`; the Overview gets there through
 * `overviewStartMismatch` below. Both call THIS, so the two cannot drift apart.
 *
 * ⚠️ Exact label match, deliberately not `lib/laborHours`' trimmed,
 * lower-cased set that also accepts 'Cancelled'. This is the grid's comparison
 * as it has always been, and the gap-analysis SQL mirrors it
 * (`ps.label NOT IN ('Complete', 'Canceled')`); a looser rule here would drop a
 * job from the card that the grid still shows. By label, never a status id —
 * statuses are seeded and matched by label in this repo.
 *
 * ⚠️ A job with NO status is not finished. The grid keeps it and so does this.
 */
export function isFinishedJobStatus(label: string | null | undefined): boolean {
  return label === 'Complete' || label === 'Canceled';
}

/**
 * The Overview's start-mismatch flag: `startMismatch`, except on a finished job.
 *
 * ⚠️ The status comes from the crew summary, the SAME read as the two dates,
 * for the reason the dates already share one: a value off the unpolled
 * `['project', id]` cache held against ones polled every five minutes can pair
 * two different moments. The grid has this for free — its status and both dates
 * arrive on one project-options row.
 *
 * `projectStatusLabel` is only the fallback for an API that predates the
 * summary's `statusLabel`. A `null` there from the server means "no status" and
 * is used as-is rather than replaced by the fallback.
 */
export function overviewStartMismatch(
  summary: {
    plannedStart?: string | null;
    firstCrewDate?: string | null;
    statusLabel?: string | null;
  } | null | undefined,
  projectStatusLabel: string | null | undefined,
): StartMismatch | null {
  if (!summary) return null;
  const status = summary.statusLabel !== undefined ? summary.statusLabel : projectStatusLabel;
  if (isFinishedJobStatus(status)) return null;
  return startMismatch(summary.plannedStart ?? null, summary.firstCrewDate ?? null);
}

/**
 * One sentence naming the disagreement, for a tooltip or a banner.
 *
 * ⚠️ Says what each side holds and never which one is wrong — the whole point is
 * that the code cannot know. The PM may have re-planned without telling the
 * Scheduler, or the Scheduler may be working to something the PM has not
 * recorded, and the copy has to leave both open.
 */
export function startMismatchSummary(m: StartMismatch, fmt: (iso: string) => string): string {
  const days = Math.abs(m.driftDays);
  const unit = days === 1 ? 'day' : 'days';
  return m.direction === 'crew-early'
    ? `Crew is booked from ${fmt(m.firstCrewDate)}, ${days} ${unit} before the planned start of ${fmt(m.plannedStart)}.`
    : `Planned start is ${fmt(m.plannedStart)}, but no crew is booked until ${fmt(m.firstCrewDate)} — ${days} ${unit} later.`;
}
